import type { ReactNode } from 'react';
import { CheckCircle2, AlertCircle, LogOut } from 'lucide-react';

import Button from './Button';

export type ConnectionStatus = 'connected' | 'disconnected' | 'connecting';

type ConnectBarProps = {
  status: ConnectionStatus;
  /** Название подключения, например «Jira» или «GitHub». */
  label: string;
  /** Дополнительная инфа (например, аккаунт). */
  hint?: ReactNode;
  /** CTA для подключения, отображается когда disconnected. */
  onConnect?: () => void;
  /** Кнопка выхода, отображается когда connected. */
  onDisconnect?: () => void;
  /** Кастомный текст CTA «Подключить». */
  connectLabel?: string;
};

/**
 * Универсальная плашка статуса подключения для модулей-источников.
 * Отображается между шапкой страницы и контентом.
 * Если status=connected — компактная подсказка с зелёной точкой.
 * Если disconnected — заметная полоса с CTA.
 */
const ConnectBar = ({
  status,
  label,
  hint,
  onConnect,
  onDisconnect,
  connectLabel,
}: ConnectBarProps) => {
  const isConnected = status === 'connected';
  const isConnecting = status === 'connecting';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 16px',
        background: isConnected ? 'var(--shell-accent-subtle)' : 'var(--shell-bg-subtle)',
        borderBottom: '1px solid var(--shell-border-subtle)',
        flexShrink: 0,
        fontSize: 13,
        color: 'var(--shell-text-primary)',
      }}
    >
      {isConnected ? (
        <CheckCircle2 size={14} strokeWidth={2} color="var(--shell-accent-primary)" />
      ) : (
        <AlertCircle
          size={14}
          strokeWidth={2}
          color={isConnecting ? 'var(--shell-text-tertiary)' : 'var(--shell-semantic-warning)'}
        />
      )}
      <span style={{ fontWeight: 500 }}>
        {isConnected
          ? `Подключено: ${label}`
          : isConnecting
            ? `Подключение к ${label}…`
            : `Не подключено к ${label}`}
      </span>
      {hint && <span style={{ color: 'var(--shell-text-tertiary)', fontSize: 12 }}>{hint}</span>}
      <div style={{ flex: 1 }} />
      {isConnected && onDisconnect && (
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<LogOut size={12} strokeWidth={1.75} />}
          onClick={onDisconnect}
        >
          Отключить
        </Button>
      )}
      {!isConnected && onConnect && (
        <Button variant="primary" size="sm" onClick={onConnect} disabled={isConnecting}>
          {connectLabel ?? 'Подключить'}
        </Button>
      )}
    </div>
  );
};

export default ConnectBar;
